import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import {
  DriverFilterOptions,
  DriverRoleFilterOption,
  PlantFilterOption,
  RegionalFilterOption,
  GroupFilterOption,
  EmailFilterOption
} from "@shared/interfaces/driver-filter-options.interface";
import { Driver } from "@shared/interfaces/driver.interface";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, ToastMessages, DriverModuleMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Organisms
import GenericQueryFilter, {
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
// endregion Imports - Organisms

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    returnedData: Driver[]
  ) => void;
}
// endregion Interfaces

const QueryFilterDrivers: React.FC<IProps> = ({ open, onClose, onFilter }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsRoleFilter, setOptionsRoleFilter] = useState<DriverRoleFilterOption[]>([]);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<PlantFilterOption[]>([]);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<RegionalFilterOption[]>([]);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<GroupFilterOption[]>([]);
  const [optionsEmailFilter, setOptionsEmailFilter] = useState<EmailFilterOption[]>([]);

  const [loadingRoleFilter, setLoadingRoleFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);
  const [loadingEmailFilter, setLoadingEmailFilter] = useState(true);

  const [loadingDrivers, setLoadingDrivers] = useState(false);
  // endregion useState
  // region Constants
  const paramNameRole = ParamNameTypes.ID_ROLES;
  const paramNamePlant = ParamNameTypes.ID_PLANTS;
  const paramNameGroups = ParamNameTypes.ID_GROUPS;
  const paramNameRegional = ParamNameTypes.ID_REGIONALS;
  const paramNameEmails = ParamNameTypes.RESPONSIBLE_EMAILS;

  const roleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRoleFilter,
    labelName: t(DriverModuleMessages.driverRole),
    paramName: paramNameRole,
    values: optionsRoleFilter.map(
      (role) => ({
        value: role.id_driver_role!,
        label: role.description!
      })
    )
  }), [loadingRoleFilter, optionsRoleFilter, paramNameRole, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant.id_plant!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, paramNamePlant, t]);

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(FilterMessages.optionRegionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map(
      (regional) => ({
        value: regional.id_regional!,
        label: `${regional.code} - ${regional.name}`
      })
    )
  }), [loadingRegionalFilter, optionsRegionalFilter, paramNameRegional, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(FilterMessages.optionGroups),
    paramName: paramNameGroups,
    values: optionsGroupFilter.map(
      (group) => ({
        value: group.id_group!,
        label: `${group.code} - ${group.name}`
      })
    )
  }), [loadingGroupFilter, optionsGroupFilter, paramNameGroups, t]);

  const emailFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingEmailFilter,
    labelName: t(DriverModuleMessages.responsibleEmails),
    paramName: paramNameEmails,
    values: optionsEmailFilter.map((user) => ({
      value: user.email!,
      label: `${user.email} - ${user.name}`
    }))
  }), [loadingEmailFilter, optionsEmailFilter, paramNameEmails, t]);

  // endregion Constants
  // region Refs
  const paramsFilterRef = useRef<IParamQueryValues[]>([]);
  // endregion Refs
  // region Functions

  /**
   * Load filter options from the API.
   */
  const loadFilterOptions = async () => {

    try {

      const { data }: { data: StandardReturnObject<DriverFilterOptions> } = await api.get("drivers/filter-options");

      if (data.status === "success") {
        setOptionsRoleFilter(data.result?.roles ?? []);
        setOptionsPlantFilter(data.result?.plants ?? []);
        setOptionsRegionalFilter(data.result?.regionals ?? []);
        setOptionsGroupFilter(data.result?.groups ?? []);
        setOptionsEmailFilter(data.result?.emails ?? []);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingRoleFilter(false);
      setLoadingPlantFilter(false);
      setLoadingRegionalFilter(false);
      setLoadingGroupFilter(false);
      setLoadingEmailFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[]) => {

    setLoadingDrivers(true);

    paramsFilterRef.current = paramsData;

    try {

      const mappedParams = paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      const { data } = await api.post("drivers/get-filtered-by", mappedParams);

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));

      if (data.status !== "success") addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      onFilter(
        validParamsData.length,
        data.result ?? [] as Driver[]
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingDrivers(false);
    }
  };
  // endregion Functions
  // region Effects

  // Load options from the API
  useEffect(() => {

    loadFilterOptions().then();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingDrivers} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.DRIVERS}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          roleFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions,
          emailFilterOptions
        ]}
      />
    </>
  );
};

export default QueryFilterDrivers;
